import {
  SkyBadge,
  SkyCard,
  SkyDataGrid,
  SkyMessageBar,
  SkyText,
  SkyTooltip,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useId, type CSSProperties } from "react";
import type { PlatformHealth } from "@shared/types/health";
import { EMPTY_CELL, formatAgo, formatCount } from "@/components/platform/format";
import { ErrorState, GridSkeleton } from "@/components/platform/ScreenState";
import { usePlatformHealth } from "@/hooks/use-platform-health";
import { useNow } from "@/hooks/use-now";
import { DefinitionIcon } from "@/lib/icons";
import { pixelColumnLayout } from "@/lib/grid";

// copy-jargon-file-ok: the platform health tab is the operator's view of the runner, the queue and the migrations. (#929, scripts/check-copy.mjs)

/**
 * The Health tab (FR-71, canvas 14 panel 04).
 *
 * Two rules the shapes already encode and this tab has to keep. First, **a
 * count this deployment could not read is `null`, never `0`** — so it renders
 * as "unknown", because on a dashboard a wrong zero reads as a healthy idle
 * queue, which is the one reading that stops an operator looking further.
 *
 * Second, health state is a WORD in a tile — Healthy, Degraded, Stale — not a
 * coloured dot. The colour repeats the word; it never carries it alone.
 *
 * The stale-scan reading measures the scan's FOOTPRINT rather than its clock:
 * the last scan time lives in the runner process and the web app cannot see it,
 * so what is reported is what a stalled scanner leaves behind — schedules whose
 * firing time has passed and that nobody has taken.
 *
 * Round 6 A2 and walk W9 (owner, 2026-10-01: "it's not clear which instance is for which tenant
 * and what is queue depth"): each per-instance row names its tenant, muted, before the instance
 * ("Tenant · Instance", as the switcher reads), with the slug under it, and "queue depth" is split
 * into what it was hiding: Running, Waiting to start and Steps not picked up (`stepsReady`, the
 * jobs due now; a running import's timed re-check is not counted). Every number is defined twice:
 * on an info icon by each title (`SkyDataGrid` headers are plain strings, so a header cannot take
 * a hover) and in an always-visible list under the table (a touch screen cannot hover). With more
 * than eight instances, each tenant gets a small table of its own under its name.
 */

type InstanceRow = PlatformHealth["perInstance"][number];

interface HealthRow {
  id: string;
  check: string;
  result: string;
  detail: string;
  tone: "success" | "warning" | "danger" | "subtle";
}

/** The plain-words definitions (round 6 A2, the brief's sentences), one per number. */
export const HEALTH_DEFINITIONS = [
  {
    term: "Running",
    text: "Imports and transformations that have started and not finished.",
  },
  {
    term: "Waiting to start",
    text: "Runs created but not started yet, usually waiting for a free runner slot.",
  },
  {
    term: "Steps not picked up",
    text: "Scheduled work the runner has not taken yet. A growing number means the runner is behind.",
  },
  {
    term: "Overdue schedules",
    text: "Schedules whose next run time has passed.",
  },
  {
    term: "Last firing",
    text: "When a schedule of the instance last started a run.",
  },
] as const;

const DEFINITION_OF = Object.fromEntries(
  HEALTH_DEFINITIONS.map((entry) => [entry.term, entry.text]),
) as Record<(typeof HEALTH_DEFINITIONS)[number]["term"], string>;

const TILE_DEFINITIONS = {
  web: "The web app that serves this page. It is never in the run path: imports and schedules carry on without it.",
  database: "The platform database and whether every migration this release needs is applied.",
  queue: DEFINITION_OF["Steps not picked up"],
  scan: "Whether the runner takes schedules on time. It is stale when a schedule has waited past its run time longer than the threshold.",
} as const;

const PER_INSTANCE_DEFINITION =
  "Per instance: Running, Waiting to start, Steps not picked up, Overdue schedules and Last firing for each active instance. What each one means is listed under the table.";

/** More instances than this and each tenant gets a table of its own (round 6 A2). */
const SINGLE_TABLE_MAX = 8;

/**
 * Each column's minimum: its header in Chromium plus the 32 px padding, the tenant and instance
 * cell wider for two names. 1,056 px in all, inside the 1,131 px pane at 1,440 px.
 */
const MIN_COLUMN_WIDTH: Record<string, number> = {
  instance: 240,
  runsRunning: 120,
  runsWaiting: 168,
  stepsReady: 200,
  overdueSchedules: 184,
  lastFiredAt: 144,
};

/**
 * An info icon whose definition shows on hover and focus (`SkyTooltip`), with the same sentence
 * in `sr-only` for a screen reader. The trigger passes `aria-describedby={undefined}` so the
 * sentence is not read twice while the tooltip is open (client/AGENTS.md, the cell hint rule).
 */
function Definition({ text, testId }: { text: string; testId: string }) {
  return (
    <SkyTooltip content={text}>
      <span
        tabIndex={0}
        aria-describedby={undefined}
        className="inline-flex items-center text-muted-foreground"
        data-testid={testId}
      >
        <DefinitionIcon className="size-4" aria-hidden="true" />
        <span className="sr-only">{text}</span>
      </span>
    </SkyTooltip>
  );
}

function Tile({
  title,
  definition,
  state,
  detail,
  tone,
  testId,
}: {
  title: string;
  definition: string;
  state: string;
  detail: string;
  tone: "success" | "warning" | "danger" | "informative";
  testId: string;
}) {
  return (
    <SkyCard appearance="outline" size="small" testId={testId}>
      <div className="flex flex-col gap-1 p-1">
        <div className="flex items-center gap-1">
          <SkyText size={200} color="muted" block>
            {title}
          </SkyText>
          <Definition text={definition} testId={`${testId}-info`} />
        </div>
        <div data-testid={`${testId}-state`} data-tone={tone}>
          <SkyBadge appearance="tint" color={tone}>
            {state}
          </SkyBadge>
        </div>
        <SkyText size={200} color="muted" block wrap>
          {detail}
        </SkyText>
      </div>
    </SkyCard>
  );
}

type StatusTone = "success" | "warning" | "danger";

function migrationsTone(status: PlatformHealth["migrations"]["status"]): StatusTone {
  if (status === "healthy") return "success";
  return status === "pending" ? "warning" : "danger";
}

/** The due scan's state in words: Healthy, Stale, or Unavailable. */
function scanWords(status: PlatformHealth["scan"]["status"]): string {
  if (status === "healthy") return "Healthy";
  return status === "stale" ? "Stale" : "Unavailable";
}

function scanTone(status: PlatformHealth["scan"]["status"]): StatusTone {
  if (status === "healthy") return "success";
  return status === "stale" ? "warning" : "danger";
}

function rowsFor(health: PlatformHealth, nowMs: number): HealthRow[] {
  return [
    {
      id: "database",
      check: "Platform database",
      result: health.database === "healthy" ? "Healthy" : "Unavailable",
      detail: EMPTY_CELL,
      tone: health.database === "healthy" ? "success" : "danger",
    },
    {
      id: "migrations",
      check: "Migrations",
      result:
        health.migrations.status === "healthy"
          ? "Up to date"
          : health.migrations.status.charAt(0).toUpperCase() + health.migrations.status.slice(1),
      detail: `${health.migrations.applied} applied · ${health.migrations.pending} pending · ${health.migrations.failures} failed`,
      tone: migrationsTone(health.migrations.status),
    },
    {
      // The deployment-wide queue the unauthenticated probe reports: every job not yet done,
      // timed re-checks of running imports included, so it is not "Steps not picked up".
      id: "queue",
      check: "Runner queue",
      result: health.queue.status === "healthy" ? "Healthy" : "Unavailable",
      detail: `${formatCount(health.queue.total)} jobs queued, timed re-checks included · ${formatCount(health.queue.active)} being worked · ${health.queue.databases.probed} databases probed, ${health.queue.databases.unavailable} unreachable`,
      tone: health.queue.status === "healthy" ? "success" : "danger",
    },
    {
      id: "scan",
      check: "Due scan",
      result: scanWords(health.scan.status),
      detail: `${formatCount(health.scan.overdueSchedules)} overdue of ${formatCount(health.scan.enabledSchedules)} enabled · last firing ${formatAgo(health.scan.lastFiredAt, nowMs)} · threshold ${health.scan.thresholdSeconds} s`,
      tone: scanTone(health.scan.status),
    },
    {
      id: "instances",
      check: "Instances",
      result: `${health.instances.active} of ${health.instances.total} active`,
      detail: `${health.instances.provisioning} provisioning · ${health.instances.disabled} disabled`,
      tone: "subtle",
    },
  ];
}

/**
 * The sum of a per-instance count across the rows that could be read; null when none could
 * (and the deployment has instances), so an unreadable total is unknown, never zero.
 */
function sumKnown(rows: readonly InstanceRow[], pick: (row: InstanceRow) => number | null) {
  const known = rows.map(pick).filter((value): value is number => value !== null);
  if (rows.length > 0 && known.length === 0) return null;
  return known.reduce((sum, value) => sum + value, 0);
}

/** The queue tile (round 6 A2): the steps due now and not taken, across every instance. */
function stepsTile(health: PlatformHealth) {
  const rows = health.perInstance;
  const steps = health.queue.status === "unavailable" ? null : sumKnown(rows, (r) => r.stepsReady);
  const oldest = rows.reduce<number | null>(
    (max, row) =>
      row.oldestReadySeconds === null ? max : Math.max(max ?? 0, row.oldestReadySeconds),
    null,
  );
  const behind = oldest !== null && oldest > health.scan.thresholdSeconds;
  const running = sumKnown(rows, (r) => r.runsRunning);
  const waiting = sumKnown(rows, (r) => r.runsWaiting);
  return {
    state: formatCount(steps),
    tone: steps === null || behind ? ("warning" as const) : ("success" as const),
    detail: `${formatCount(running)} running · ${formatCount(waiting)} waiting to start`,
  };
}

function countCell(value: number | null, testId: string) {
  return (
    <SkyText size={200} font="numeric" testId={testId}>
      {formatCount(value)}
    </SkyText>
  );
}

function instanceColumns(showTenant: boolean, nowMs: number): SkyDataGridColumn<InstanceRow>[] {
  return [
    {
      key: "instance",
      // Inside one tenant's own table the cell holds the instance alone (the tenant is the
      // table's heading), so the header says so (r6admin review hand-over).
      header: showTenant ? "Tenant · Instance" : "Instance",
      width: "1%",
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col" data-testid={`health-instance-${row.instanceId}`}>
          <SkyText
            size={200}
            block
            truncate
            title={showTenant ? `${row.tenantDisplayName} · ${row.name}` : row.name}
          >
            {showTenant ? (
              <>
                <span
                  className="text-muted-foreground"
                  data-testid={`health-instance-tenant-${row.instanceId}`}
                >
                  {row.tenantDisplayName}
                </span>
                <span className="text-muted-foreground" aria-hidden="true">
                  {" · "}
                </span>
              </>
            ) : null}
            <span className="font-medium" data-testid={`health-instance-name-${row.instanceId}`}>
              {row.name}
            </span>
          </SkyText>
          <SkyText
            size={200}
            color="muted"
            font="monospace"
            block
            truncate
            title={row.slug}
            testId={`health-instance-slug-${row.instanceId}`}
          >
            {row.slug}
          </SkyText>
        </div>
      ),
    },
    {
      key: "runsRunning",
      header: "Running",
      align: "right",
      width: "1%",
      renderCell: (_value, row) => countCell(row.runsRunning, `health-running-${row.instanceId}`),
    },
    {
      key: "runsWaiting",
      header: "Waiting to start",
      align: "right",
      width: "1%",
      renderCell: (_value, row) => countCell(row.runsWaiting, `health-waiting-${row.instanceId}`),
    },
    {
      key: "stepsReady",
      header: "Steps not picked up",
      align: "right",
      width: "1%",
      renderCell: (_value, row) => countCell(row.stepsReady, `health-steps-${row.instanceId}`),
    },
    {
      key: "overdueSchedules",
      header: "Overdue schedules",
      align: "right",
      width: "1%",
      renderCell: (_value, row) =>
        countCell(row.overdueSchedules, `health-overdue-${row.instanceId}`),
    },
    {
      key: "lastFiredAt",
      header: "Last firing",
      width: "1%",
      renderCell: (_value, row) => (
        <SkyText size={200}>{formatAgo(row.lastFiredAt, nowMs)}</SkyText>
      ),
    },
  ];
}

function InstanceGrid({
  rows,
  showTenant,
  label,
  testId,
}: {
  rows: InstanceRow[];
  showTenant: boolean;
  label: string;
  testId: string;
}) {
  const nowMs = useNow().getTime();
  const layout = pixelColumnLayout(instanceColumns(showTenant, nowMs), {
    minimums: MIN_COLUMN_WIDTH,
    selection: false,
  });
  return (
    <div style={{ "--health-grid-floor": `${layout.floor}px` } as CSSProperties}>
      <SkyDataGrid<InstanceRow>
        data={rows}
        columns={layout.columns}
        getRowId={(row) => row.instanceId}
        ariaLabel={label}
        className="[&_table]:min-w-(--health-grid-floor)"
        testId={testId}
      />
    </div>
  );
}

/** Rows grouped by tenant, in the server's order (tenant, then instance). */
function byTenant(rows: readonly InstanceRow[]) {
  const groups = new Map<string, { name: string; rows: InstanceRow[] }>();
  for (const row of rows) {
    const group = groups.get(row.tenantId) ?? { name: row.tenantDisplayName, rows: [] };
    group.rows.push(row);
    groups.set(row.tenantId, group);
  }
  return [...groups.entries()].map(([tenantId, group]) => ({ tenantId, ...group }));
}

function PerInstance({ rows }: { rows: InstanceRow[] }) {
  const headingId = useId();
  return (
    <section className="flex flex-col gap-2" aria-labelledby={headingId}>
      <div className="flex items-center gap-1">
        <SkyText as="h2" id={headingId} size={400} weight="semibold" block>
          Per instance
        </SkyText>
        <Definition text={PER_INSTANCE_DEFINITION} testId="health-per-instance-info" />
      </div>
      {rows.length > SINGLE_TABLE_MAX ? (
        byTenant(rows).map((group) => (
          <div
            key={group.tenantId}
            className="flex flex-col gap-1"
            data-testid={`health-tenant-${group.tenantId}`}
          >
            <SkyText as="h3" size={300} weight="semibold" block>
              {group.name}
            </SkyText>
            <InstanceGrid
              rows={group.rows}
              showTenant={false}
              label={`Instances of ${group.name}`}
              testId={`health-tenant-grid-${group.tenantId}`}
            />
          </div>
        ))
      ) : (
        <InstanceGrid
          rows={rows}
          showTenant
          label="Health of each instance"
          testId="health-per-instance-grid"
        />
      )}
    </section>
  );
}

function Definitions() {
  const headingId = useId();
  return (
    <section
      className="flex flex-col gap-1"
      aria-labelledby={headingId}
      data-testid="health-definitions"
    >
      <SkyText as="h2" id={headingId} size={400} weight="semibold" block>
        What these numbers mean
      </SkyText>
      <dl className="flex flex-col gap-1">
        {HEALTH_DEFINITIONS.map((entry) => (
          <div key={entry.term} className="flex flex-wrap gap-x-1">
            <dt>
              <SkyText size={200} weight="semibold">
                {`${entry.term}:`}
              </SkyText>
            </dt>
            <dd>
              <SkyText size={200} color="muted">
                {entry.text}
              </SkyText>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function PlatformHealthTab({ enabled }: { enabled: boolean }) {
  const health = usePlatformHealth(enabled);
  // #1260: "last firing 14 min ago" moves on its own, also when a poll returns the same data.
  const nowMs = useNow().getTime();

  if (health.isPending) {
    return <GridSkeleton rows={4} label="Reading platform health…" testId="health-loading" />;
  }

  if (health.isError) {
    return (
      <ErrorState
        title="Platform health could not be read"
        error={health.error}
        onRetry={() => void health.refetch()}
        testId="health-error"
      />
    );
  }

  const data = health.data as PlatformHealth;
  const rows = rowsFor(data, nowMs);
  const steps = stepsTile(data);

  const columns: SkyDataGridColumn<HealthRow>[] = [
    { key: "check", header: "Check", width: "26%" },
    {
      key: "result",
      header: "Result",
      width: "22%",
      renderCell: (_value, row) => (
        <SkyBadge appearance="tint" color={row.tone}>
          {row.result}
        </SkyBadge>
      ),
    },
    {
      key: "detail",
      header: "Detail",
      width: "52%",
      // Walk 2026-10-02, D20: the grid's own text cell cut the queue's sentence ("... 6 databa...").
      renderCell: (_value, row) => (
        <SkyText size={200} block testId={`health-check-detail-${row.id}`}>
          {row.detail}
        </SkyText>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="health-tab">
      {data.scan.status === "stale" ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="The due scan is behind"
          testId="health-scan-stale"
          message={`${formatCount(data.scan.overdueSchedules)} enabled schedules are past their firing time, the oldest by ${formatCount(data.scan.oldestOverdueSeconds)} seconds. Runs due since then start late. Check the runner's own health endpoint and its container logs.`}
        />
      ) : null}

      {data.queue.status === "unavailable" || data.queue.databases.unavailable > 0 ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="Some tenant databases did not answer"
          testId="health-queue-degraded"
          message={`${data.queue.databases.unavailable} of ${data.queue.databases.probed + data.queue.databases.unavailable} databases were unreachable, so the queue counts below are partial. A count that could not be read is shown as unknown, never as zero.`}
        />
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          testId="health-tile-web"
          title="Web"
          definition={TILE_DEFINITIONS.web}
          state="Healthy"
          tone="success"
          detail={`Up for ${Math.round(data.web.uptimeSeconds / 60)} minutes`}
        />
        <Tile
          testId="health-tile-database"
          title="Database"
          definition={TILE_DEFINITIONS.database}
          state={data.database === "healthy" ? "Healthy" : "Unavailable"}
          tone={data.database === "healthy" ? "success" : "danger"}
          detail={`${data.migrations.applied} migrations applied, ${data.migrations.pending} pending`}
        />
        <Tile
          testId="health-tile-queue"
          title="Steps not picked up"
          definition={TILE_DEFINITIONS.queue}
          state={steps.state}
          tone={steps.tone}
          detail={steps.detail}
        />
        <Tile
          testId="health-tile-scan"
          title="Due scan"
          definition={TILE_DEFINITIONS.scan}
          state={scanWords(data.scan.status)}
          tone={scanTone(data.scan.status)}
          detail={`Last firing ${formatAgo(data.scan.lastFiredAt, nowMs)}`}
        />
      </div>

      <SkyDataGrid<HealthRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        ariaLabel="Platform checks"
        testId="health-grid"
      />

      {data.perInstance.length > 0 ? <PerInstance rows={data.perInstance} /> : null}

      <Definitions />

      <SkyText size={200} color="muted" block>
        Re-read every 30 seconds while this tab is open. The tiles summarise. The tables are the
        detail.
      </SkyText>
    </div>
  );
}
